/**
 * Shell del panel: navegación, estado del sistema y acceso.
 *
 * La navegación sólo muestra lo que el rol puede hacer. No es cosmético: un
 * enlace que lleva a un 403 hace parecer roto el sistema, y el analista de
 * sólo lectura no debería ver botones de edición que no puede usar.
 */

import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import { StatusChip } from './charts';

interface NavItem {
  to: string;
  label: string;
  permission: string;
}

const NAV_GROUPS: { group: string; items: NavItem[] }[] = [
  {
    group: 'Operación',
    items: [
      { to: '/', label: 'Monitoreo', permission: 'monitoring:read' },
      { to: '/asignaciones', label: 'Asignaciones', permission: 'assignments:read' },
      { to: '/jornada', label: 'Jornada de campo', permission: 'monitoring:read' },
      { to: '/resultados', label: 'Resultados', permission: 'monitoring:read' },
      { to: '/entrevistas', label: 'Entrevistas', permission: 'interviews:read-all' },
    ],
  },
  {
    group: 'Calidad',
    items: [
      { to: '/calidad', label: 'Alertas y supervisión', permission: 'quality:read' },
      { to: '/codificacion', label: 'Codificación', permission: 'coding:read' },
    ],
  },
  {
    group: 'Diseño',
    items: [
      { to: '/marco', label: 'Marco muestral', permission: 'frame:read' },
      { to: '/cartografia', label: 'Cartografía y GPS', permission: 'cartography:read' },
      { to: '/instrumento', label: 'Cuestionario', permission: 'instrument:read' },
    ],
  },
  {
    group: 'Administración',
    items: [
      { to: '/usuarios', label: 'Usuarios', permission: 'users:read' },
      { to: '/gafetes', label: 'Gafetes', permission: 'users:read' },
      { to: '/exportacion', label: 'Exportación', permission: 'export:data' },
      { to: '/consumo', label: 'Consumo de datos', permission: 'monitoring:read' },
      { to: '/bitacora', label: 'Bitácora', permission: 'monitoring:read' },
    ],
  },
];

export function Shell({ children }: { children: React.ReactNode }) {
  const { operator, logout, can } = useAdmin();
  const navigate = useNavigate();

  const health = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    refetchInterval: 60_000,
  });

  const cartography = useQuery({
    queryKey: ['cartography', 'status'],
    queryFn: api.cartographyStatus,
    enabled: can('cartography:read'),
  });

  const pii = useQuery({
    queryKey: ['quality', 'pii', 'status'],
    queryFn: api.piiStatus,
    enabled: can('pii:read'),
  });

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          {/* El archivo del logotipo lo decide el CSS según el tema; aquí sólo
              se nombra la marca para quien use lector de pantalla. */}
          <div className="logo" role="img" aria-label="Calimetría" />
          <div className="sidebar__sub">Sondeo Tecate 2027</div>
        </div>

        <nav className="sidebar__nav">
          {NAV_GROUPS.map((group) => {
            const visible = group.items.filter((item) => can(item.permission));
            if (visible.length === 0) return null;
            return (
              <div key={group.group}>
                <div className="sidebar__group">{group.group}</div>
                {visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
                    }
                  >
                    {item.label}
                    {item.to === '/cartografia' &&
                    cartography.data &&
                    (cartography.data.coverage.missingFromCartography.length > 0 ||
                      cartography.data.coverage.unverifiedGroups.length > 0) ? (
                      <span className="chip__icon" style={{ color: 'var(--status-warning)' }} aria-label="requiere atención">
                        ▲
                      </span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar__foot">
        {/* Estado del sistema: lo que debe verse sin tener que buscarlo. */}
        <div className="stack" style={{ fontSize: 12 }}>
          {health.data ? (
            <StatusChip status={health.data.database ? 'good' : 'critical'}>
              Base {health.data.database ? 'conectada' : 'sin conexión'}
            </StatusChip>
          ) : null}

          {health.data ? (
            <StatusChip status={health.data.gpsDetection === 'postgis' ? 'good' : 'warning'}>
              GPS: {health.data.gpsDetection === 'postgis' ? 'PostGIS' : 'respaldo en Node'}
            </StatusChip>
          ) : null}

          {pii.data && pii.data.withContact > 0 ? (
            <StatusChip status="neutral">{pii.data.withContact} contactos vigentes</StatusChip>
          ) : null}
        </div>

        <div className="sidebar__session">
          <div className="strong">{operator?.name}</div>
          <div className="faint">{operator?.roleLabel}</div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 8, paddingLeft: 0 }}
            onClick={() => {
              void logout().then(() => navigate('/', { replace: true }));
            }}
          >
            Cerrar sesión
          </button>
        </div>
        </div>
      </aside>

      <main className="main">{children}</main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Acceso                                    */
/* -------------------------------------------------------------------------- */

export function LoginPage() {
  const { login } = useAdmin();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se pudo iniciar sesión.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center-screen">
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 className="page-title">Panel CAPI</h1>
          <p className="page-sub">Sondeo Pre-Electoral de Tecate 2027 · Calimetría</p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="identifier">
            Código de operador o correo
          </label>
          <input
            id="identifier"
            className="input"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            autoCapitalize="characters"
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="password">
            Contraseña
          </label>
          <input
            id="password"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error ? <div className="callout callout--critical">{error}</div> : null}

        <button type="submit" className="btn btn--primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>

        <p className="faint" style={{ marginTop: 18, textAlign: 'center' }}>
          Las cuentas las crea el superadministrador. No hay registro propio ni recuperación por
          correo.
        </p>
      </form>
    </div>
  );
}

export function ChangePasswordPage() {
  const { markPasswordChanged, operator } = useAdmin();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (next !== confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      markPasswordChanged();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? (e.fieldErrors ? Object.values(e.fieldErrors).flat().join(' ') : e.message)
          : 'No se pudo cambiar la contraseña.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center-screen">
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 380 }}>
        <h1 className="page-title">Cambia tu contraseña</h1>
        <p className="page-sub">
          {operator?.name}, la contraseña que te entregaron es temporal. Elige una propia para
          continuar.
        </p>

        <div className="field" style={{ marginTop: 20 }}>
          <label className="field__label" htmlFor="current">
            Contraseña actual
          </label>
          <input
            id="current"
            className="input"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="next">
            Contraseña nueva
          </label>
          <input
            id="next"
            className="input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            minLength={10}
            required
          />
          <p className="faint" style={{ marginTop: 5 }}>
            Al menos 10 caracteres, con una letra y un número.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="confirm">
            Repite la contraseña nueva
          </label>
          <input
            id="confirm"
            className="input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>

        {error ? <div className="callout callout--critical">{error}</div> : null}

        <button type="submit" className="btn btn--primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar y continuar'}
        </button>
      </form>
    </div>
  );
}

/** Pantalla para rutas que el rol no puede ver. */
export function Forbidden() {
  return (
    <div className="callout callout--warning">
      <span className="callout__title">Sin permiso</span>
      Tu rol no tiene acceso a esta sección. Si lo necesitas, pídelo al coordinador.
    </div>
  );
}

export function useRefetchOnFocus(): void {
  useEffect(() => {
    const handler = () => undefined;
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, []);
}
